/**
 * GraphDebugPanel.tsx
 * ────────────────────
 * Panel de debug du Legal Graph — visible uniquement pour les admins.
 * Affiche l'analyse exhaustive retournée par GET /check-graph.
 *
 * Usage dans GraphStatusBanner :
 *   <GraphDebugPanel report={report} onRebuild={triggerRebuild} />
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import type { GraphCheckResult } from '@/hooks/useGraphCheck';

interface FieldStatus {
  value:  unknown;
  status: 'ok' | 'warning' | 'error' | 'info';
  detail: string;
  [key: string]: unknown;
}

interface Props {
  report:    GraphCheckResult & { fields?: Record<string, FieldStatus>; rebuild_will_do?: string[] };
  onRebuild: () => void;
}

// ── Config visuelle ───────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  ok:      { icon: '✓', classes: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/20' },
  warning: { icon: '⚠', classes: 'text-amber-600  dark:text-amber-400',   bg: 'bg-amber-50  dark:bg-amber-950/20'  },
  error:   { icon: '✗', classes: 'text-red-600    dark:text-red-400',     bg: 'bg-red-50    dark:bg-red-950/20'    },
  info:    { icon: 'ℹ', classes: 'text-blue-600   dark:text-blue-400',    bg: 'bg-blue-50   dark:bg-blue-950/20'   },
};

const FIELD_LABELS: Record<string, string> = {
  judilibre_id:             'judilibre_id',
  ecli:                     'ECLI',
  number:                   'number',
  numbers:                  'numbers[]',
  faits:                    'faits (LLM)',
  procedure:                'procédure (LLM)',
  probleme:                 'problème (LLM)',
  solution_text:            'solution_text (LLM)',
  quality_score:            'quality_score',
  publication:              'publication',
  location:                 'location (CA)',
  source_text:              'texte source (jurisprudences)',
  graph_built_at:           'graph_built_at',
  contested:                'contested (Judilibre)',
  rapprochements:           'rapprochements (Judilibre)',
  procedure_graph:          'procedure_graph[]',
  jonctions:                'jonctions',
  renvoi_apres_cassation:   'renvoi_apres_cassation',
  cited_by:                 'cited_by',
  importance_score:         'importance_score',
  graph_extracted_at:       'graph_extracted_at',
  cite_arrets_qualifies:    'cite_arrets_qualifies[]',
  arrets_impactes:          'arrets_impactes[]',
  procedure_lineage:        'procedure_lineage{}',
  concepts:                 'concepts[]',
  mots_cles:                'mots_cles[]',
  articles:                 'articles[]',
  domaine:                  'domaine',
};

// Libellés contenant du texte (hors identifiants techniques) → clés i18n
const FIELD_LABEL_KEYS: Record<string, string> = {
  faits:       'graph.debug.field.faits',
  procedure:   'graph.debug.field.procedure',
  probleme:    'graph.debug.field.probleme',
  source_text: 'graph.debug.field.source_text',
};

// Groupes de champs par pipeline
const FIELD_GROUPS = [
  {
    label:  'graph.debug.group_ingest',
    color:  'border-blue-200 dark:border-blue-800',
    header: 'bg-blue-50 dark:bg-blue-950/30',
    fields: ['judilibre_id', 'ecli', 'number', 'numbers', 'faits', 'procedure',
             'probleme', 'solution_text', 'quality_score', 'publication', 'location', 'source_text'],
  },
  {
    label:  'graph.debug.group_procedure',
    color:  'border-indigo-200 dark:border-indigo-800',
    header: 'bg-indigo-50 dark:bg-indigo-950/30',
    fields: ['graph_built_at', 'contested', 'rapprochements', 'procedure_graph',
             'jonctions', 'renvoi_apres_cassation', 'cited_by', 'importance_score'],
  },
  {
    label:  'graph.debug.group_llm',
    color:  'border-purple-200 dark:border-purple-800',
    header: 'bg-purple-50 dark:bg-purple-950/30',
    fields: ['graph_extracted_at', 'cite_arrets_qualifies', 'arrets_impactes',
             'procedure_lineage', 'concepts', 'mots_cles', 'articles', 'domaine'],
  },
];

// ── FieldRow ──────────────────────────────────────────────────────────────────

function FieldRow({ name, field }: { name: string; field: FieldStatus }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const cfg  = STATUS_CONFIG[field.status] || STATUS_CONFIG.info;
  const label = FIELD_LABEL_KEYS[name] ? t(FIELD_LABEL_KEYS[name]) : (FIELD_LABELS[name] || name);

  // Contenu expandable : links du procedure_graph, joints, etc.
  const hasDetail = name === 'procedure_graph' && Array.isArray((field as any).links)
    || name === 'jonctions' && Array.isArray((field as any).missing_joints);

  return (
    <div className={`rounded-lg border px-3 py-2 ${cfg.bg} border-transparent`}>
      <div className="flex items-start justify-between gap-2 cursor-pointer"
           onClick={() => hasDetail && setExpanded(v => !v)}>
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <span className={`text-xs font-bold mt-0.5 shrink-0 ${cfg.classes}`}>{cfg.icon}</span>
          <div className="min-w-0">
            <span className="text-xs font-mono text-foreground">{label}</span>
            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{field.detail}</p>
          </div>
        </div>
        {hasDetail && (
          <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">
            {expanded ? '▲' : '▼'}
          </span>
        )}
      </div>

      {/* Liens du procedure_graph */}
      {expanded && name === 'procedure_graph' && Array.isArray((field as any).links) && (
        <div className="mt-2 space-y-1 pl-4 border-l border-border">
          {(field as any).links.map((link: any, i: number) => (
            <div key={i} className={`text-[10px] font-mono px-2 py-1 rounded ${
              link.issues?.length ? 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300'
                                  : 'bg-muted/50 text-muted-foreground'
            }`}>
              <span className="font-bold">{link.link_type}</span>
              {' → '}{link.target || t('graph.debug.empty')}
              {link.source && <span className="opacity-60"> · {link.source}</span>}
              {link.issues?.length > 0 && (
                <span className="ml-1 text-red-600 font-bold">⚠ {link.issues.join(', ')}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Joints manquants */}
      {expanded && name === 'jonctions' && (field as any).missing_joints?.length > 0 && (
        <div className="mt-2 pl-4 border-l border-border">
          <p className="text-[10px] text-amber-600 font-medium">{t('graph.debug.missing_links')}</p>
          {(field as any).missing_joints.map((n: string, i: number) => (
            <p key={i} className="text-[10px] font-mono text-muted-foreground">{n}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export function GraphDebugPanel({ report, onRebuild }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const fields       = report.fields || {};
  const anomalies    = report.anomalies || [];
  const rebuildTodo  = report.rebuild_will_do || [];

  const errorCount   = Object.values(fields).filter(f => f.status === 'error').length;
  const warningCount = Object.values(fields).filter(f => f.status === 'warning').length;

  return (
    <div className="mt-2">
      {/* Bouton toggle */}
      <button
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        {t('graph.debug.analyze')}
        {(errorCount > 0 || warningCount > 0) && (
          <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
            errorCount > 0
              ? 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400'
              : 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400'
          }`}>
            {errorCount > 0 ? t('graph.debug.errors', { count: errorCount }) : t('graph.debug.warnings', { count: warningCount })}
          </span>
        )}
        <span className="text-[10px] opacity-50">{open ? '▲' : '▼'}</span>
      </button>

      {/* Panel expandable */}
      {open && (
        <div className="mt-3 rounded-xl border border-border bg-card overflow-hidden">

          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-foreground">{t('graph.debug.title')}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t('graph.debug.summary', {
                  links: report.procedure_graph_size,
                  anomalies: anomalies.length,
                  time: new Date(report.checked_at).toLocaleTimeString(getDateLocale()),
                })}
              </p>
            </div>
            <button
              onClick={onRebuild}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('graph.status.rebuild')}
            </button>
          </div>

          <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">

            {/* Ce que le rebuild va faire */}
            {rebuildTodo.length > 0 && (
              <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/20 p-3">
                <p className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300 mb-2">
                  🔧 {t('graph.debug.rebuild_todo')}
                </p>
                <ul className="space-y-1">
                  {rebuildTodo.map((item, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-indigo-700 dark:text-indigo-300">
                      <span className="shrink-0 mt-0.5">→</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Anomalies summary */}
            {anomalies.length > 0 && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-3">
                <p className="text-[11px] font-bold text-red-700 dark:text-red-300 mb-2">
                  ⚠ {t('graph.debug.anomalies', { n: anomalies.length })}
                </p>
                <ul className="space-y-1">
                  {anomalies.map((a, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-red-700 dark:text-red-300">
                      <span className={`shrink-0 mt-0.5 ${a.fixable ? 'text-amber-500' : 'text-red-500'}`}>
                        {a.fixable ? '🔧' : '⛔'}
                      </span>
                      <span>
                        <span className="font-mono">[{a.type}]</span> {a.detail}
                        {!a.fixable && <span className="ml-1 text-[9px] opacity-60">{t('graph.debug.not_fixable')}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Champs par groupe */}
            {Object.keys(fields).length > 0 && FIELD_GROUPS.map(group => {
              const groupFields = group.fields.filter(f => fields[f]);
              if (groupFields.length === 0) return null;
              return (
                <div key={group.label} className={`rounded-lg border ${group.color} overflow-hidden`}>
                  <div className={`px-3 py-2 ${group.header}`}>
                    <p className="text-[11px] font-bold text-foreground">{t(group.label)}</p>
                  </div>
                  <div className="p-2 space-y-1.5">
                    {groupFields.map(fieldName => (
                      <FieldRow key={fieldName} name={fieldName} field={fields[fieldName]} />
                    ))}
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      )}
    </div>
  );
}